/* eslint-disable no-plusplus */
/* eslint-disable consistent-return */
import { dynamicEditorComponent } from '@doltech/utils/lib/dynamicWithProps/dynamicEditorComponent';
import * as React from 'react';
import { Editor, Element, Node, Transforms } from 'slate';
import { ReactEditor, RenderElementProps } from 'slate-react';

import { SlatePlugin } from '../../../../slatePlugins/slatePlugins.types';
import { canDeleteNode, getParentNode } from '../shared-transforms';
import { CardLogicPluginOptions } from './CardLogicPlugin.types';
import {
  CARD_LOGIC_CELL_ELEMENT_TYPE,
  CARD_LOGIC_COLUMN_ELEMENT_TYPE,
  CARD_LOGIC_ELEMENT_TYPE,
  CARD_LOGIC_HEADER_CELL_ELEMENT_TYPE,
  generateSingleCardLogicCell,
  generateSingleCardLogicHeaderCell,
  generateSingleColumnLogicCell,
} from './model/constants';
import { withDynamicBlogEditorPluginHydration } from '@doltech/core/lib/hooks/withDolHydrationOnDemand';
import { FAQ_ANSWER, FAQ_QUESTION } from '../FAQ/types';
import { CardLogicReadOnlyPlugin } from './CardLogicReadOnlyPlugin';

const CardLogicElementEditable = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(() =>
    import(/* webpackExports: "CardLogicElementEditable" */'./Element/CardLogicElement').then((mod) => mod.CardLogicElementEditable)
  )
);
const CardLogicCellElementEditable = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(
    () =>
      import(/* webpackExports: "CardLogicCellElementEditable" */'./Element/Cell/CardLogicCellElement').then((mod) => mod.CardLogicCellElementEditable),
    {
      dependsOn: CardLogicElementEditable,
    }
  )
);
const CardLogicColumnElementEditable = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(
    () =>
      import(/* webpackExports: "CardLogicColumnElementEditable" */'./Element/Cell/CardLogicColumnElement').then(
        (mod) => mod.CardLogicColumnElementEditable
      ),
    {
      dependsOn: CardLogicElementEditable,
    }
  )
);
const CardLogicHeaderCellElementEditable = withDynamicBlogEditorPluginHydration()(
  dynamicEditorComponent(
    () =>
      import(/* webpackExports: "CardLogicHeaderCellElementEditable" */'./Element/Header/CardLogicHeaderCellElement').then(
        (mod) => mod.CardLogicHeaderCellElementEditable
      ),
    {
      dependsOn: CardLogicElementEditable,
    }
  )
);

export const CardLogicPlugin = (options: CardLogicPluginOptions = {}): SlatePlugin => {
  if (options.isReadOnly) {
    return CardLogicReadOnlyPlugin();
  }
  return {
    renderElement: (renderProps: RenderElementProps) => {
      const { element } = renderProps;
      switch (element.type) {
        case CARD_LOGIC_ELEMENT_TYPE: {
          return <CardLogicElementEditable {...options} {...renderProps} />;
        }
        case CARD_LOGIC_COLUMN_ELEMENT_TYPE: {
          return <CardLogicColumnElementEditable {...options} {...renderProps} />;
        }
        case CARD_LOGIC_HEADER_CELL_ELEMENT_TYPE: {
          return <CardLogicHeaderCellElementEditable {...options} {...renderProps} />;
        }
        case CARD_LOGIC_CELL_ELEMENT_TYPE: {
          return <CardLogicCellElementEditable {...options} {...renderProps} />;
        }
        default:
      }
    },
  };
};

const matchCellNode = (node) =>
  [
    CARD_LOGIC_CELL_ELEMENT_TYPE,
    CARD_LOGIC_HEADER_CELL_ELEMENT_TYPE,
    FAQ_QUESTION,
    FAQ_ANSWER,
  ].includes(node?.type as any);

export const withCardLogicPluginEditorChain =
  () =>
  <T extends ReactEditor>(editor: T) => {
    const { insertBreak, normalizeNode, deleteBackward, deleteForward } = editor;

    editor.normalizeNode = (entry) => {
      const [node, path] = entry as [any, any[]];

      if (Element.isElement(node)) {
        switch (node?.type) {
          case CARD_LOGIC_HEADER_CELL_ELEMENT_TYPE:
          case CARD_LOGIC_CELL_ELEMENT_TYPE: {
            const parentNode = getParentNode({ node, path });
            // Ensure card logic cell parent always column
            if (
              path.length === 1 ||
              (parentNode && parentNode.type !== CARD_LOGIC_COLUMN_ELEMENT_TYPE)
            ) {
              Transforms.delete(editor, { at: path });
            }
            break;
          }
          case CARD_LOGIC_ELEMENT_TYPE: {
            let columnCount = 0;

            (node?.children || []).forEach((item) => {
              if (item.type === CARD_LOGIC_COLUMN_ELEMENT_TYPE) {
                columnCount++;
              }
            });

            // Ensure two card logic column exists
            const [lastChildNode] = Node.children(editor, path);
            if (lastChildNode && columnCount < 2) {
              const [, lastChildNodePath] = lastChildNode;
              Transforms.insertNodes(editor, generateSingleColumnLogicCell(), {
                at: lastChildNodePath,
              });
              return;
            }

            // Unwrap node that is not CardLogiColumn
            for (const [child, childPath] of Node.children(editor, path)) {
              if (
                Element.isElement(child) &&
                [CARD_LOGIC_COLUMN_ELEMENT_TYPE].includes(child?.type as string) === false
              ) {
                Transforms.unwrapNodes(editor, { at: childPath });
                return;
              }
            }

            break;
          }
          case CARD_LOGIC_COLUMN_ELEMENT_TYPE: {
            let headerCount = 0;
            let cellCount = 0;

            (node?.children || []).forEach((item) => {
              if (item.type === CARD_LOGIC_HEADER_CELL_ELEMENT_TYPE) {
                headerCount++;
              }
              if (item.type === CARD_LOGIC_CELL_ELEMENT_TYPE) {
                cellCount++;
              }
            });

            const [lastChildNode] = Node.children(editor, path);

            // Move header node to top
            for (const [child, childPath] of Node.children(editor, path)) {
              if (
                Element.isElement(child) &&
                [CARD_LOGIC_HEADER_CELL_ELEMENT_TYPE].includes(child?.type as string) &&
                childPath[childPath.length - 1] > 0
              ) {
                const newPath = [...childPath];
                newPath[newPath.length - 1] = 0;
                Transforms.moveNodes(editor, {
                  at: childPath,
                  to: newPath,
                });
                return;
              }
            }

            if (lastChildNode) {
              const [, lastChildPath] = lastChildNode;
              // Ensure only one header cell exist inside column
              if (headerCount > 1) {
                for (const [child, childPath] of Node.children(editor, path)) {
                  if (
                    Element.isElement(child) &&
                    [CARD_LOGIC_HEADER_CELL_ELEMENT_TYPE].includes(child?.type as string)
                  ) {
                    Transforms.delete(editor, { at: childPath });
                    return;
                  }
                }
              }

              if (headerCount < 1) {
                Transforms.insertNodes(editor, generateSingleCardLogicHeaderCell(), {
                  at: lastChildPath,
                });
                return;
              }

              // Ensure at least one cell existence
              if (cellCount < 1) {
                Transforms.insertNodes(editor, generateSingleCardLogicCell(), {
                  at: lastChildPath,
                });
                return;
              }
            }

            // Unwrap node that is not CardLogicCell
            for (const [child, childPath] of Node.children(editor, path)) {
              if (
                Element.isElement(child) &&
                [CARD_LOGIC_HEADER_CELL_ELEMENT_TYPE, CARD_LOGIC_CELL_ELEMENT_TYPE].includes(
                  child?.type as string
                ) === false
              ) {
                Transforms.unwrapNodes(editor, { at: childPath });
                return;
              }
            }

            break;
          }
        }
      }
      normalizeNode(entry);
    };

    editor.deleteBackward = (unit) => {
      const canDelete = canDeleteNode({
        editor,
        unit,
        direction: 'backward',
        match: matchCellNode,
      });
      if (!canDelete) {
        return;
      }
      deleteBackward(unit);
    };

    editor.deleteForward = (unit) => {
      const canDelete = canDeleteNode({
        editor,
        unit,
        direction: 'forward',
        match: matchCellNode,
      });
      if (!canDelete) {
        return;
      }
      deleteForward(unit);
    };

    editor.insertBreak = () => {
      const { selection } = editor;

      if (selection) {
        const [table] = Editor.nodes(editor, {
          match: (n: any) =>
            !Editor.isEditor(n) && Element.isElement(n) && n.type === CARD_LOGIC_ELEMENT_TYPE,
        });

        if (table) {
          editor.insertText('\n');
          return;
        }
      }

      insertBreak();
    };

    return editor;
  };
